import { useCallback, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  NodeMouseHandler,
  BackgroundVariant,
  Connection,
  ReactFlowInstance,
  EdgeMouseHandler
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { Box } from '@mui/material';
import CustomNode from '@/src/components/common/graph-components-assets/CustomNode';
import CustomEdge from '@/src/components/common/graph-components-assets/CustomEdge';

const defaultNodeTypes = {
  custom: CustomNode
};

const defaultEdgeTypes = {
  custom: CustomEdge
};

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

export type directiveGraphPropsType = {
  initialNodes: Node[];
  initialEdges: Edge[];
  nodeWidth?: number;
  nodeHeight?: number;
  nodeTypes?: Record<string, React.FC>;
  edgeTypes?: Record<string, React.FC>;
  onNodeClick?: NodeMouseHandler;
  onNodeMouseEnter?: (node: Node) => void;
  onNodeMouseLeave?: (node: Node) => void;
  onEdgeClick?: EdgeMouseHandler;
  onEdgeMouseEnter?: EdgeMouseHandler;
  onEdgeMouseLeave?: EdgeMouseHandler;
  onNodeConnect?: (source: string | null, target: string | null) => void;
  deletableNodes?: Array<Node['id']>;
  deletableEdges?: Array<Node['id']>;
  isDeletableNodesAndEdges?: boolean;
  isNodeConnectable?: boolean;
  isDisableNode?: boolean;
  backgroundVariant?: BackgroundVariant;
  backgroundColor?: string;
  hoveredEdgeId?: string | null;
};

const DirectiveGraph = (props: directiveGraphPropsType) => {
  const {
    deletableNodes,
    deletableEdges,
    isNodeConnectable = true,
    isDisableNode = false,
    isDeletableNodesAndEdges = false,
    initialEdges,
    initialNodes,
    nodeHeight = 80,
    nodeTypes = defaultNodeTypes,
    edgeTypes = defaultEdgeTypes,
    nodeWidth = 150,
    onEdgeClick,
    onEdgeMouseEnter,
    onEdgeMouseLeave,
    onNodeClick,
    onNodeConnect,
    onNodeMouseEnter,
    onNodeMouseLeave,
    backgroundVariant = BackgroundVariant.Dots,
    backgroundColor = '#F7FAFC',
    hoveredEdgeId
  } = props;

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const findChildrenNodes = useCallback(
    (nodeId: string, visited = new Set<string>()): string[] => {
      if (visited.has(nodeId)) return [];
      visited.add(nodeId);

      const directChildren = edges
        .filter((edge) => edge.source === nodeId)
        .map((edge) => edge.target);

      const allChildren = [...directChildren];

      directChildren.forEach((childId) => {
        const grandchildren = findChildrenNodes(childId, visited);
        allChildren.push(...grandchildren);
      });

      return allChildren;
    },
    [edges]
  );

  const handleNodeMouseEnter: NodeMouseHandler = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (isDisableNode) return;
      const childrenIds = findChildrenNodes(node.id);

      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === node.id) {
            return { ...n, data: { ...n.data, isHovered: true } };
          } else if (childrenIds.includes(n.id)) {
            return { ...n, data: { ...n.data, isChild: true } };
          }
          return { ...n, data: { ...n.data, isHovered: false, isChild: false } };
        })
      );
      setEdges((eds) =>
        eds.map((e) => {
          if (
            e.source === node.id ||
            (childrenIds.includes(e.target) && childrenIds.includes(e.source))
          ) {
            return { ...e, animated: true, style: { stroke: '#2241A6' } };
          }
          return { ...e, animated: false, style: { stroke: '#DADADA' } };
        })
      );
      if (onNodeMouseEnter) {
        onNodeMouseEnter(node);
      }
    },
    [findChildrenNodes, setNodes, setEdges, onNodeMouseEnter, isDisableNode]
  );

  const handleNodeMouseLeave: NodeMouseHandler = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (isDisableNode) return;
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          data: { ...n.data, isHovered: false, isChild: false }
        }))
      );
      setEdges((eds) =>
        eds.map((e) => ({
          ...e,
          animated: false,
          style: { stroke: '#DADADA' }
        }))
      );
      if (onNodeMouseLeave) {
        onNodeMouseLeave(node);
      }
    },
    [setEdges, setNodes, onNodeMouseLeave, isDisableNode]
  );

  const handleNodeConnect = useCallback(
    (connection: Connection) => {
      if (onNodeConnect) {
        onNodeConnect(connection?.source, connection?.target);
      }
    },
    [onNodeConnect]
  );

  const handleEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (onEdgeClick && edge.deletable) {
        onEdgeClick(event, edge);
      }
    },
    [onEdgeClick]
  );

  const handleEdgeMouseEnterCallback = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (onEdgeMouseEnter && edge.deletable) {
        onEdgeMouseEnter(event, edge);
      }
    },
    [onEdgeMouseEnter]
  );

  const handleEdgeMouseLeaveCallback = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (onEdgeMouseLeave) {
        onEdgeMouseLeave(event, edge);
      }
    },
    [onEdgeMouseLeave]
  );

  const getLayoutElements = useCallback(
    (nds: Node[], eds: Edge[], direction: 'TB' | 'LR' = 'TB'): Node[] => {
      dagreGraph.setGraph({ rankdir: direction });

      nds.forEach((node: Node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
      });

      eds.forEach((edge: Edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
      });

      dagre.layout(dagreGraph);
      return nds.map((node: Node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        node.position = {
          x: nodeWithPosition.x - nodeWidth / 2,
          y: nodeWithPosition.y - nodeHeight / 2
        };
        // Mark nodes as deletable based on deletableNodes prop
        if (deletableNodes?.includes(node.id)) {
          node.deletable = true;
        } else {
          node.deletable = false;
        }

        return node;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deletableNodes, isDisableNode]
  );

  const getEdgesLayout = (edges: Edge[]) => {
    return edges.map((edge) => {
      const isDeletable = isDeletableNodesAndEdges || deletableEdges?.includes(edge.id);
      const isNew = edge.data?.isNew;

      // Determine edge color based on state (no hover state in initial layout)
      let edgeColor = '#DADADA'; // Default gray for existing edges
      if (isNew) {
        edgeColor = '#4CAF50'; // Green for new edges
      }

      return {
        ...edge,
        type: 'custom',
        deletable: isDeletable,
        data: {
          ...edge.data,
          deletable: isDeletable,
          hovered: false, // No hover state in initial layout
          isNew: isNew,
          onClick: (event: React.MouseEvent) => {
            if (isDeletable && onEdgeClick) {
              onEdgeClick(event, edge);
            }
          }
        },
        style: {
          ...edge.style,
          stroke: edgeColor,
          strokeWidth: isNew ? 2 : 1,
          cursor: 'default'
        }
      };
    });
  };

  const handleInit = useCallback((instance: ReactFlowInstance) => {
    instance.fitView({ padding: 0.2, minZoom: 0.5, maxZoom: 0.5 });
  }, []);

  // Combined effect for layout and styling - runs when nodes/edges change or hover state changes
  useEffect(() => {
    const layoutNodes = getLayoutElements(initialNodes, initialEdges);
    const layoutEdges = getEdgesLayout(initialEdges);

    // Apply styling to edges based on current hover state
    const styledEdges = layoutEdges.map((edge) => {
      const isDeletable = isDeletableNodesAndEdges || deletableEdges?.includes(edge.id);
      const isHovered = hoveredEdgeId === edge.id;
      const isNew = edge.data?.isNew;

      // Determine edge color based on state
      let edgeColor = '#DADADA'; // Default gray for existing edges
      if (isHovered) {
        edgeColor = '#ff4444'; // Red when hovered for deletion
      } else if (isNew) {
        edgeColor = '#4CAF50'; // Green for new edges
      }

      return {
        ...edge,
        data: {
          ...edge.data,
          deletable: isDeletable,
          hovered: isHovered,
          isNew: isNew,
          onClick: (event: React.MouseEvent) => {
            if (isDeletable && onEdgeClick) {
              onEdgeClick(event, edge);
            }
          }
        },
        style: {
          ...edge.style,
          stroke: edgeColor,
          strokeWidth: isHovered ? 3 : isNew ? 2 : 1,
          cursor: isHovered ? 'pointer' : 'default'
        }
      };
    });

    setNodes(layoutNodes);
    setEdges(styledEdges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletableNodes, initialNodes, initialEdges, isDeletableNodesAndEdges, hoveredEdgeId]);

  const handleNodeClick: NodeMouseHandler = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (!isDisableNode && onNodeClick) {
        onNodeClick(event, node);
      }
    },
    [isDisableNode, onNodeClick]
  );

  return (
    <Box sx={{ display: 'flex', height: '70vh', width: '100%' }}>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeMouseEnter={handleNodeMouseEnter}
          onNodeMouseLeave={handleNodeMouseLeave}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick}
          onEdgeMouseEnter={handleEdgeMouseEnterCallback}
          onEdgeMouseLeave={handleEdgeMouseLeaveCallback}
          onConnect={handleNodeConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          onInit={handleInit}
          minZoom={0.5}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 50, zoom: 0.5 }}
          attributionPosition={'top-left'}
          nodesConnectable={isNodeConnectable && !isDisableNode}
          deleteKeyCode={isDeletableNodesAndEdges ? [] : null}
          proOptions={{ hideAttribution: true }}
        >
          <Controls />
          <MiniMap nodeStrokeWidth={5} zoomable pannable position="bottom-right" />
          <Background gap={16} color={backgroundColor} variant={backgroundVariant} />
        </ReactFlow>
      </Box>
    </Box>
  );
};

export default DirectiveGraph;
